import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../App';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import {
  Calendar, Sparkles, Loader2, CheckCircle2, AlertTriangle,
  ChevronDown, ChevronUp, ArrowRight, Users, Clock,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AIScheduleBuilder = () => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [mode, setMode] = useState('fill_gaps');
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null);
  const [showShifts, setShowShifts] = useState(false);
  const [showCoverage, setShowCoverage] = useState(false);

  // Quick range shortcuts
  const setRange = (days) => {
    const today = new Date();
    const start = new Date(today);
    start.setDate(start.getDate() + (1 + (7 - start.getDay()) % 7)); // Next Monday
    const end = new Date(start);
    end.setDate(end.getDate() + days - 1);
    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(end.toISOString().split('T')[0]);
  };

  const generate = async () => {
    if (!startDate || !endDate) { toast.error('Select a date range'); return; }
    setLoading(true);
    setResult(null);
    try {
      const res = await axios.post(
        `${API}/ai/auto-schedule?start_date=${startDate}&end_date=${endDate}&mode=${mode}`,
        {},
        { headers }
      );
      if (res.data.error) {
        toast.error(res.data.error);
      } else {
        setResult(res.data);
        toast.success('Schedule proposal ready');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate');
    } finally {
      setLoading(false);
    }
  };

  const applySchedule = async () => {
    if (!result?.proposed_shifts?.length) return;
    setApplying(true);
    try {
      const res = await axios.post(
        `${API}/ai/apply-schedule`,
        { shifts: result.proposed_shifts },
        { headers }
      );
      const data = res.data;
      toast.success(`Applied ${data.applied} shifts (${data.skipped} already existed)`);
      if (data.errors?.length) {
        toast.warning(`${data.errors.length} shifts had errors`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to apply schedule');
    } finally {
      setApplying(false);
    }
  };

  // Group shifts by date for visual display
  const shiftsByDate = {};
  if (result?.proposed_shifts) {
    result.proposed_shifts.forEach(s => {
      if (!shiftsByDate[s.date]) shiftsByDate[s.date] = [];
      shiftsByDate[s.date].push(s);
    });
  }

  const formatDate = (d) => {
    const dt = new Date(d + 'T12:00:00');
    return dt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  return (
    <Card data-testid="ai-schedule-builder">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Calendar className="h-4 w-4 text-indigo-500" />
          AI Schedule Builder
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Quick Ranges */}
        <div className="flex flex-wrap gap-2">
          <span className="text-xs text-slate-500 self-center mr-1">Quick:</span>
          {[
            { label: 'Next Week', days: 5 },
            { label: 'Next 2 Weeks', days: 10 },
            { label: 'Next Month', days: 22 },
          ].map(q => (
            <Button
              key={q.label}
              variant="outline"
              size="sm"
              className="h-7 text-xs"
              onClick={() => setRange(q.days)}
              data-testid={`quick-${q.days}`}
            >
              {q.label}
            </Button>
          ))}
        </div>

        {/* Mode Selector */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500">Mode:</span>
          <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5">
            <button
              onClick={() => setMode('fill_gaps')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                mode === 'fill_gaps'
                  ? 'bg-indigo-600 text-white shadow-sm'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
              }`}
              data-testid="mode-fill-gaps"
            >
              Fill Gaps
            </button>
            <button
              onClick={() => setMode('full_rebuild')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                mode === 'full_rebuild'
                  ? 'bg-indigo-600 text-white shadow-sm'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
              }`}
              data-testid="mode-full-rebuild"
            >
              Full Schedule
            </button>
          </div>
        </div>
        <p className="text-xs text-slate-400 -mt-1 w-full">
          {mode === 'fill_gaps'
            ? 'Adds shifts where coverage is below historical norms or minimums'
            : 'Generates a complete schedule from scratch for all employees'}
        </p>

        {/* Date Inputs + Generate */}
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Start</label>
            <Input
              type="date"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
              className="h-9 text-sm"
              data-testid="builder-start"
            />
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1 block">End</label>
            <Input
              type="date"
              value={endDate}
              onChange={e => setEndDate(e.target.value)}
              className="h-9 text-sm"
              data-testid="builder-end"
            />
          </div>
          <Button
            onClick={generate}
            disabled={loading}
            className="bg-indigo-600 hover:bg-indigo-500 h-9"
            data-testid="generate-schedule-btn"
          >
            {loading ? (
              <><Loader2 className="h-4 w-4 animate-spin mr-1.5" />Generating...</>
            ) : (
              <><Sparkles className="h-4 w-4 mr-1.5" />Generate</>
            )}
          </Button>
        </div>

        {/* Results */}
        {result && !result.error && (
          <div className="space-y-4 pt-2 border-t border-slate-100">
            {/* Summary Row */}
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-900">{result.summary}</p>
                <p className="text-xs text-slate-500 mt-0.5">
                  {result.proposed_shifts?.length || 0} shifts across {Object.keys(shiftsByDate).length} days
                </p>
              </div>
              {result.fairness_score != null && (
                <div className="flex items-center gap-1.5 bg-slate-50 rounded-lg px-3 py-1.5">
                  <span className="text-xs text-slate-500">Fairness</span>
                  <span className={`text-sm font-bold ${
                    result.fairness_score >= 80 ? 'text-emerald-600' :
                    result.fairness_score >= 60 ? 'text-amber-600' : 'text-red-600'
                  }`} data-testid="fairness-score">{result.fairness_score}</span>
                </div>
              )}
            </div>

            {/* Warnings */}
            {result.warnings?.length > 0 && (
              <div className="space-y-1.5">
                {result.warnings.map((w, i) => (
                  <div key={i} className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2">
                    <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />{w}
                  </div>
                ))}
              </div>
            )}

            {/* Daily Coverage Overview */}
            {result.daily_coverage?.length > 0 && (
              <div>
                <button
                  onClick={() => setShowCoverage(!showCoverage)}
                  className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2"
                  data-testid="toggle-coverage"
                >
                  {showCoverage ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  Daily Coverage
                </button>
                {showCoverage && (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
                    {result.daily_coverage.map((dc, i) => (
                      <div key={i} className="bg-slate-50 rounded-lg p-2.5 text-center">
                        <p className="text-[10px] text-slate-500">{formatDate(dc.date)}</p>
                        <p className="text-lg font-bold text-slate-800">{dc.total}</p>
                        {dc.by_role && (
                          <div className="flex flex-wrap gap-1 justify-center mt-1">
                            {Object.entries(dc.by_role).map(([r, c]) => (
                              <span key={r} className="text-[10px] px-1.5 py-0.5 rounded bg-white text-slate-600 capitalize">
                                {r}: {c}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Shifts by Date */}
            <div>
              <button
                onClick={() => setShowShifts(!showShifts)}
                className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2"
                data-testid="toggle-shifts"
              >
                {showShifts ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                Proposed Shifts ({result.proposed_shifts?.length || 0})
              </button>
              {showShifts && (
                <div className="space-y-3 max-h-80 overflow-y-auto">
                  {Object.entries(shiftsByDate).sort(([a], [b]) => a.localeCompare(b)).map(([date, shifts]) => (
                    <div key={date} className="rounded-lg border border-slate-200 overflow-hidden">
                      <div className="bg-slate-50 px-3 py-1.5 border-b border-slate-200 flex items-center justify-between">
                        <span className="text-xs font-semibold text-slate-700">{formatDate(date)}</span>
                        <Badge variant="secondary" className="text-[10px]">{shifts.length} shifts</Badge>
                      </div>
                      <div className="divide-y divide-slate-100">
                        {shifts.map((s, i) => (
                          <div key={i} className="px-3 py-2 flex items-center justify-between text-xs">
                            <div className="flex items-center gap-2">
                              <Users className="h-3 w-3 text-slate-400" />
                              <span className="font-medium text-slate-800">{s.user_name}</span>
                              <Badge variant="outline" className="text-[10px] capitalize">{s.role || s.shift_type}</Badge>
                            </div>
                            <div className="flex items-center gap-2 text-slate-500">
                              <Clock className="h-3 w-3" />
                              <span>{s.start_time} - {s.end_time}</span>
                              {s.is_remote && <Badge className="text-[10px] bg-cyan-100 text-cyan-700">Remote</Badge>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Apply Button */}
            <Button
              onClick={applySchedule}
              disabled={applying || !result.proposed_shifts?.length}
              className="w-full bg-emerald-600 hover:bg-emerald-500"
              data-testid="apply-schedule-btn"
            >
              {applying ? (
                <><Loader2 className="h-4 w-4 animate-spin mr-1.5" />Applying...</>
              ) : (
                <>
                  <CheckCircle2 className="h-4 w-4 mr-1.5" />
                  Apply {result.proposed_shifts?.length || 0} Shifts to Schedule
                  <ArrowRight className="h-4 w-4 ml-1.5" />
                </>
              )}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AIScheduleBuilder;
